import { useState, useEffect, useRef } from 'react'
import { NextSeo } from 'next-seo'
import { InView, useInView } from 'react-intersection-observer'
import { Typography, Drawer } from '@evocolabs/orkaui'
import theme from '@evocolabs/orkaui/lib/theme.cjs'
import ChevronRightSmall24 from '@evocolabs/icons/lib/ChevronRightSmall24'
import Footer from '@/src/components/Footer/Footer2'
import styles from '@/components/scss/TroubleShooting.module.scss'
import Header from '@/src/components/HeaderV2'

import { parse2UpperDevice, parseSection } from '@/src/utils/troubleshooting.js'
import { removeBlueToothSymbol } from '@/src/utils/replaceBlueToothHelper'

const deviceMap = {
  'Orka One': 'orka_one',
  'Orka Two': 'orka_two'
}

export async function getStaticProps() {
  const res = await fetch(
    process.env.NEXT_PUBLIC_BASEURL + 'troubleshooting-article/'
  )
  const data = await res.json()
  return { props: { data }, revalidate: 600 }
}

const CategoriesButton = ({ sections, curSection, setCurSection }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef()
  return (
    <>
      <div
        className={styles.categories_button}
        onTouchMove={(event) => {
          ref.current &&
            (ref.current.style.top = event.touches[0].clientY + 'px')
        }}
        ref={ref}
        onClick={() => setOpen(true)}
      >
        <div className={styles.categories_button_icon}>
          <span />
          <span />
          <span />
          <span />
        </div>
        <Typography variant="Body3" color={theme.palette.primary[700]}>
          Categories
        </Typography>
      </div>
      <Drawer
        open={open}
        onClose={() => {
          setOpen(false)
        }}
      >
        <Typography variant="h3" color={theme.palette.grey[800]}>
          Categories
        </Typography>
        <nav className={styles.mobile_nav}>
          {sections.map((section, index) => (
            <Typography
              variant="Body2"
              color={theme.palette.grey[600]}
              key={index}
              className={curSection === section ? styles.selected : ''}
              sx={{ cursor: 'pointer' }}
              onClick={() => {
                setOpen(false)
                document
                  .getElementById(section)
                  .scrollIntoView({ behavior: 'smooth', block: 'center' })
              }}
            >
              {section}
            </Typography>
          ))}
        </nav>
      </Drawer>
    </>
  )
}

const TroubleShootingHeader = () => {
  return (
    <div className={styles.header}>
      <Typography variant="h1" color="#FFF" textAlign="center">
        Troubleshooting
      </Typography>
      <Typography variant="body1" color="#FFF" textAlign="center">
        Providing easy solutions for hearing aid issues.
      </Typography>
    </div>
  )
}

const TroubleShootingBody = ({ data }) => {
  const [device, setDevice] = useState('orka_two')
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const queryDevice = urlParams.get('device')
    if (queryDevice) {
      setDevice(deviceMap[queryDevice])
    }
  }, [])
  const selectedData = { ...data }
  const { ref } = useInView()
  Object.keys(selectedData).forEach((key) => {
    selectedData[key] = selectedData[key].filter(
      (qst) => qst.tags.device === device
    )
  })
  Object.keys(selectedData).forEach((key) => {
    if (selectedData[key].length === 0) {
      delete selectedData[key]
    } else {
      selectedData[key].sort((a, b) => a.id - b.id)
    }
  })
  const sections = Object.keys(selectedData)
  sections.sort((a, b) => selectedData[a][0].id - selectedData[b][0].id)
  const [curSection, setCurSection] = useState(sections[0])
  useEffect(() => {
    setTimeout(() => {
      setCurSection(sections[0])
    }, 100)
  }, [])

  return (
    <>
      <div className={styles.selection}>
        <div
          className={
            styles.selection_item +
            ' ' +
            (device === 'orka_two' ? styles.selected : '')
          }
          onClick={() => setDevice('orka_two')}
        >
          <Typography variant="Body1" color={theme.palette.grey[600]}>
            Orka Two
          </Typography>
        </div>
        <div
          className={
            styles.selection_item +
            ' ' +
            (device === 'orka_one' ? styles.selected : '')
          }
          onClick={() => setDevice('orka_one')}
        >
          <Typography variant="Body1" color={theme.palette.grey[600]}>
            Orka One
          </Typography>
        </div>
      </div>
      <div className={styles.body_wrapper}>
        <div className={styles.body}>
          <CategoriesButton
            sections={sections}
            curSection={curSection}
            setCurSection={setCurSection}
          />
          <nav className={styles.pc_nav}>
            {sections.map((section, index) => (
              <Typography
                variant="Body2"
                color={theme.palette.grey[600]}
                key={index}
                className={curSection === section ? styles.selected : ''}
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  document
                    .getElementById(section)
                    .scrollIntoView({ behavior: 'smooth', block: 'center' })
                }}
              >
                {section}
              </Typography>
            ))}
          </nav>
          <main className={styles.main}>
            {sections.map((section, index) => (
              <InView
                key={index}
                as="div"
                onChange={(inView) => {
                  if (inView) setCurSection(section)
                }}
                rootMargin="-40% 0% -50% 0%"
              >
                <div key={index} className={styles.block} ref={ref}>
                  <Typography
                    variant="h3"
                    color={theme.palette.grey[800]}
                    id={section}
                  >
                    {section}
                  </Typography>
                  <div>
                    {selectedData[section].map((qst, index) => (
                      // <Link
                      //   href={`/TroubleShooting/${parse2UpperDevice(
                      //     qst.tags.device
                      //   )}/${parseSection(qst.tags.section)}/${parseQuestion(
                      //     qst.question
                      //   )}`}
                      //   key={index}
                      // >
                      <a
                        href={`/TroubleShooting/${parse2UpperDevice(
                          qst.tags.device
                        )}/${parseSection(qst.tags.section)}/${qst.id}`}
                        key={index}
                      >
                        <div
                          className={styles.question}
                          id={removeBlueToothSymbol(qst.question).replace(
                            /[\W_]+/g,
                            '_'
                          )}
                        >
                          <Typography
                            variant="Body1"
                            color={theme.palette.grey[800]}
                            id={removeBlueToothSymbol(qst.question).replace(
                              /[\W_]+/g,
                              '_'
                            )}
                            dangerouslySetInnerHTML={{ __html: qst.question }}
                          ></Typography>
                          <ChevronRightSmall24
                            forceStroke={theme.palette.grey[800]}
                          />
                        </div>
                      </a>
                      // </Link>
                    ))}
                  </div>
                </div>
              </InView>
            ))}
          </main>
        </div>
      </div>
    </>
  )
}

export default function TroubleShooting({ data }) {
  return (
    <>
      <NextSeo
        title="Troubleshooting | Orka Hearing Aids"
        description="Easy troubleshooting for common issues with hearing aids."
      />
      <Header isWhiteBg />
      <TroubleShootingHeader />
      <TroubleShootingBody data={data} />
      <Footer />
    </>
  )
}
